import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { SaveStatus } from '../../../../../shared/components/automation/SaveStatus';
import { ScheduleEditor } from '../../components/ScheduleEditor';
import { useAutomationFlowContext } from '../../contexts/AutomationFlowContext';
import { useAutoSave } from '../../hooks/useAutoSave';
import type { AutomationStepNodeData } from '../../types/stepNodes';
import { useStepRenderer } from './hooks/useStepRenderer';
import { rendererContainer, field, fieldReadOnly } from './styles/commonRendererStyles';

interface NoActionStepRendererProps {
	nodeId: string;
	data: AutomationStepNodeData;
	readOnly?: boolean;
}

type NoActionStepWithNullableSchedule = Api.INoActionAutomationStep & {
	schedule?: Api.IAutomationStepSchedule | null;
};

export function NoActionStepRenderer({ nodeId, data, readOnly: readOnlyProp }: NoActionStepRendererProps) {
	const {
		step: noActionStep,
		template,
		canEdit,
		readonly,
		trigger,
		errorMessages,
		stepData,
		stepId,
		impersonationContext,
	} = useStepRenderer<NoActionStepWithNullableSchedule>(data, readOnlyProp);
	const { updateNodeData } = useAutomationFlowContext();
	const schedule = noActionStep?.schedule ?? stepData?.schedule;

	const autoSave = useAutoSave({
		templateId: template?.id,
		enabled: !readonly,
		impersonationContext,
		onSaveError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const handleScheduleChange = React.useCallback(
		(newSchedule: Api.IAutomationStepSchedule | null) => {
			if (!noActionStep || !stepId) {
				return;
			}

			const updatedStep: NoActionStepWithNullableSchedule = {
				...noActionStep,
				schedule: newSchedule,
			};

			updateNodeData?.(nodeId, { automationStep: updatedStep });
			autoSave.saveStep(stepId, data.stepType, updatedStep as Api.IAutomationStep);
		},
		[autoSave, data.stepType, noActionStep, nodeId, stepId, updateNodeData]
	);

	return (
		<div className={css(rendererContainer)}>
			<SaveStatus
				status={autoSave.saveStatus}
				variant='simple'
				retryCount={autoSave.retryCount}
				errorMessage={autoSave.errorMessage}
			/>
			{noActionStep && (
				<ScheduleEditor
					schedule={schedule}
					trigger={trigger}
					stepType={data.stepType}
					canEdit={canEdit}
					isBusy={autoSave.saveStatus === 'saving'}
					step={noActionStep}
					index={0}
					onScheduleChange={handleScheduleChange}
					parentCaseInfo={data.parentCaseInfo}
				/>
			)}
			<div style={{ marginBottom: 4, fontSize: 14, color: '#4A4A4A' }}>Take no action.</div>
			<div
				className={css(field, fieldReadOnly)}
				style={{
					marginTop: 8,
					fontSize: 13,
					color: '#6B7280',
					fontStyle: 'italic',
				}}
			>
				This step does nothing and allows the automation to continue to the next step.
			</div>
		</div>
	);
}
